import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchShared, SHARED_KEYS } from '@shared/services/sharedResource';
import { Lock, Mail } from 'lucide-react';
import { getToken } from '@shared/services/api';

export default function SuspensionBanner() {
  const { t } = useTranslation('auth');
  const [isSuspended, setIsSuspended] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // Fetch user profile to check suspension status
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Shared with useUser and the compliance pages — one request per page
        // load instead of one per component.
        const data = await fetchShared<{ user?: { is_suspended?: boolean } }>(
          SHARED_KEYS.profile,
          async () => {
            const response = await fetch('/api/v1/auth/profile', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch profile');
            return response.json();
          }
        );

        if (data.user) {
          setIsSuspended(data.user.is_suspended || false);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();

    // Listen for suspension events from API interceptor
    const handleSuspension = () => {
      setIsSuspended(true);
      setIsVisible(true);
    };

    window.addEventListener('account-suspended', handleSuspension);
    return () => window.removeEventListener('account-suspended', handleSuspension);
  }, []);

  // Animate in after loading
  useEffect(() => {
    if (!loading && isSuspended) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [loading, isSuspended]);

  if (loading || !isSuspended) {
    return null;
  }

  return (
    <div
      id="suspension-banner"
      className={`transition-all duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden py-0 border-0'
      }`}
    >
      <div className="bg-state-danger-light dark:bg-state-danger-dark border-b-4 border-black/40 shadow-lg">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Icon and Message */}
            <div className="flex items-start sm:items-center space-x-3 flex-1 min-w-0">
              {/* Icon */}
              <div className="flex-shrink-0 p-2 rounded-lg bg-black/20 dark:bg-black/15">
                <Lock className="w-5 h-5 text-white dark:text-on-accent-dark" />
              </div>

              {/* Message */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white dark:text-on-accent-dark">
                  {t('suspension.title', 'Account Suspended')}
                </p>
                <p className="text-xs sm:text-sm mt-0.5 text-white/85 dark:text-on-accent-dark/85">
                  {t(
                    'suspension.message',
                    'Your account has been suspended. You have read-only access. Please contact support to resolve this issue.'
                  )}
                </p>
              </div>
            </div>

            {/* Contact Support Button */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <a
                href="mailto:support@pgkb.com"
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-bold transition-all hover:scale-105 active:scale-95 bg-surface-card-light text-state-danger-light hover:bg-surface-2-light shadow-lg dark:bg-surface-card-dark dark:text-state-danger-dark dark:hover:bg-surface-2-dark"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('suspension.contactSupport', 'Contact Support')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
